import type { Feature, FeatureCollection } from "geojson";
import { LngLatBounds } from "maplibre-gl";
import type { Job } from "../../../models";
import type { Mission } from "@rogoag/shared-ts";
import type { Robot } from "../../../models/rogo/Robot";
import { processStandardJobs } from "../../../application/map/jobProjection";

// ── Job FCs ─────────────────────────────────────────────────────────────────

export function buildJobFCs(jobs: Job[]) {
	if (jobs.length === 0) {
		return {
			polygonFC: EMPTY_FC,
			pointFC: EMPTY_FC,
			bounds: undefined as LngLatBounds | undefined,
			jobLookup: new Map<string, Job>(),
		};
	}
	const processed = processStandardJobs(jobs, "layer-type");
	return {
		polygonFC: fc(processed.polygonFeatures),
		pointFC: fc(processed.pointFeatures),
		bounds: processed.bounds,
		jobLookup: processed.jobLookup,
	};
}

// ── Mission FCs ─────────────────────────────────────────────────────────────

export function splitMissionFeatures(geojson: FeatureCollection) {
	const polygons: Feature[] = [];
	const samples: Feature[] = [];
	const cores: Feature[] = [];
	const lines: Feature[] = [];

	for (const f of geojson.features) {
		const type = f.geometry.type;
		if (type === "Polygon" || type === "MultiPolygon") {
			polygons.push(f);
		} else if (type === "Point" || type === "MultiPoint") {
			if (f.properties?.core_location) {
				cores.push(f);
			} else {
				samples.push(f);
			}
		} else if (type === "LineString" || type === "MultiLineString") {
			lines.push(f);
		}
	}

	return { polygons, samples, cores, lines };
}

/** For exe missions, rewrite core Point geometry to use pulled coordinates instead of planned */
function usePulledCoords(f: Feature): Feature {
	if (f.geometry.type !== "Point") return f;
	const props = f.properties ?? {};
	const lat = parseFloat(props.pulled_lat);
	const lon = parseFloat(props.pulled_lon);
	if (!isFinite(lat) || !isFinite(lon) || (lat === 0 && lon === 0)) return f;
	return {
		...f,
		geometry: { type: "Point", coordinates: [lon, lat] },
	};
}

export function buildMissionFCs(missions: Mission[], usePulled = false) {
	const allPolygons: Feature[] = [];
	const allSamples: Feature[] = [];
	const allCores: Feature[] = [];
	const allLines: Feature[] = [];

	for (let i = 0; i < missions.length; i++) {
		const mission = missions[i];
		const { polygons, samples, cores, lines } = splitMissionFeatures(mission.geojson);

		// Tag each feature with missionIndex for popup lookup
		const tag = (f: Feature) => ({
			...f,
			properties: { ...f.properties, missionIndex: i },
		});

		const transformCore = usePulled ? (f: Feature) => tag(usePulledCoords(f)) : tag;

		allPolygons.push(...polygons.map(tag));
		allSamples.push(...samples.map(tag));
		allCores.push(...cores.map(transformCore));
		allLines.push(...lines.map(tag));
	}

	return {
		polygonFC: fc(allPolygons),
		sampleFC: fc(allSamples),
		coreFC: fc(allCores),
		allPointFC: fc([...allSamples, ...allCores]),
		lineFC: fc(allLines),
	};
}

// ── Robot FCs ───────────────────────────────────────────────────────────────

export function buildRobotFCs(robots: Robot[]) {
	const isValidCoord = (lat: number, lng: number) => lat !== 0 && lng !== 0 && isFinite(lat) && isFinite(lng);

	const pointFC: FeatureCollection = {
		type: "FeatureCollection",
		features: robots
			.filter((r) => isValidCoord(r.latitude, r.longitude))
			.map((r) => ({
				type: "Feature" as const,
				geometry: {
					type: "Point" as const,
					coordinates: [r.longitude, r.latitude],
				},
				properties: {
					id: r.id,
					name: r.name,
					latitude: r.latitude,
					longitude: r.longitude,
				},
			})),
	};

	// Max degrees between consecutive points before splitting into a new segment
	const JUMP_THRESHOLD = 0.1;

	const trailFC: FeatureCollection = {
		type: "FeatureCollection",
		features: robots
			.filter((r) => r.raw && r.raw.length > 1)
			.flatMap((r) => {
				const coords = r
					.raw!.slice()
					.sort((a, b) => a.timestamp - b.timestamp)
					.map((p) => [parseFloat(p.longitude), parseFloat(p.latitude)] as [number, number])
					.filter(([lng, lat]) => isValidCoord(lat, lng));

				// Split into segments at large jumps (robot transported between sites)
				const segments: [number, number][][] = [];
				let current: [number, number][] = [];
				for (const coord of coords) {
					if (current.length > 0) {
						const prev = current[current.length - 1];
						const dlat = Math.abs(coord[1] - prev[1]);
						const dlng = Math.abs(coord[0] - prev[0]);
						if (dlat > JUMP_THRESHOLD || dlng > JUMP_THRESHOLD) {
							if (current.length > 1) segments.push(current);
							current = [];
						}
					}
					current.push(coord);
				}
				if (current.length > 1) segments.push(current);

				return segments.map((seg) => ({
					type: "Feature" as const,
					geometry: {
						type: "LineString" as const,
						coordinates: seg,
					},
					properties: { id: r.id, name: r.name },
				}));
			}),
	};

	return { pointFC, trailFC };
}

// ── Bounds ──────────────────────────────────────────────────────────────────

export function computeBounds(
	jobBounds: LngLatBounds | undefined,
	missionFCsList: ReturnType<typeof buildMissionFCs>[],
	robotFCs: ReturnType<typeof buildRobotFCs>,
): { bounds: LngLatBounds | undefined; missionBounds: LngLatBounds | undefined } {
	const bounds = jobBounds ? new LngLatBounds(jobBounds.getSouthWest(), jobBounds.getNorthEast()) : new LngLatBounds();

	const safeExtend = (lng: number, lat: number) => {
		if (isFinite(lng) && isFinite(lat) && (lng !== 0 || lat !== 0)) {
			bounds.extend([lng, lat]);
		}
	};

	const extendFromFC = (fc: FeatureCollection) => {
		for (const f of fc.features) {
			const geom = f.geometry;
			if (geom.type === "Point") {
				safeExtend(geom.coordinates[0], geom.coordinates[1]);
			} else if (geom.type === "Polygon") {
				for (const coord of geom.coordinates[0]) {
					safeExtend(coord[0], coord[1]);
				}
			} else if (geom.type === "MultiPolygon") {
				for (const poly of geom.coordinates) {
					for (const coord of poly[0]) {
						safeExtend(coord[0], coord[1]);
					}
				}
			} else if (geom.type === "LineString") {
				for (const coord of geom.coordinates) {
					safeExtend(coord[0], coord[1]);
				}
			}
		}
	};

	// Mission-only bounds (used for initial zoom priority)
	const missionBounds = new LngLatBounds();
	const safeExtendMission = (lng: number, lat: number) => {
		if (isFinite(lng) && isFinite(lat) && (lng !== 0 || lat !== 0)) missionBounds.extend([lng, lat]);
	};
	const extendMissionFromFC = (fc: FeatureCollection) => {
		for (const f of fc.features) {
			const geom = f.geometry;
			if (geom.type === "Point") safeExtendMission(geom.coordinates[0], geom.coordinates[1]);
			else if (geom.type === "Polygon") for (const coord of geom.coordinates[0]) safeExtendMission(coord[0], coord[1]);
			else if (geom.type === "MultiPolygon")
				for (const poly of geom.coordinates) for (const coord of poly[0]) safeExtendMission(coord[0], coord[1]);
			else if (geom.type === "LineString") for (const coord of geom.coordinates) safeExtendMission(coord[0], coord[1]);
		}
	};
	for (const missionFCs of missionFCsList) {
		extendMissionFromFC(missionFCs.polygonFC);
		extendMissionFromFC(missionFCs.allPointFC);
		extendMissionFromFC(missionFCs.lineFC);
	}

	let validMissionBounds: LngLatBounds | undefined;
	try {
		missionBounds.getCenter();
		validMissionBounds = missionBounds;
	} catch {
		validMissionBounds = undefined;
	}

	// Combined bounds (all data)
	for (const missionFCs of missionFCsList) {
		extendFromFC(missionFCs.polygonFC);
		extendFromFC(missionFCs.allPointFC);
		extendFromFC(missionFCs.lineFC);
	}
	extendFromFC(robotFCs.pointFC);
	extendFromFC(robotFCs.trailFC);

	let validBounds: LngLatBounds | undefined;
	try {
		bounds.getCenter();
		validBounds = bounds;
	} catch {
		validBounds = undefined;
	}

	return { bounds: validBounds, missionBounds: validMissionBounds };
}

// ── Utilities ───────────────────────────────────────────────────────────────

const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

function fc(features: Feature[]): FeatureCollection {
	return { type: "FeatureCollection", features };
}
